import * as React from 'react'

import { Box, Button } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
import TextField from 'modules/core/components/TextField'
import { useForm } from 'modules/core/hooks/useForm'
import { FormErrors } from 'modules/core/hooks/useForm'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import Loading from 'modules/finance/components/Loading'
import { useTaxTypes } from 'modules/finance/hooks/useTaxTypes'

interface NewTaxRateFormValues {
  percentage: string
  description: string
}

interface NewTaxRateModalProps {
  taxTypeId: number
  onClose: () => void
}

const NewTaxRateModal: React.FC<NewTaxRateModalProps> = ({ taxTypeId, onClose }) => {
  const { createTaxRate, data } = useTaxTypes()
  const taxType = data?.finance.taxTypes.find((t) => t.id === taxTypeId)
  const [submitting, setSubmitting] = React.useState<boolean>(false)

  const form = useForm<NewTaxRateFormValues>({
    defaultValues: {
      percentage: '',
      description: '',
    },

    validate: (data) => {
      const errors: FormErrors<NewTaxRateFormValues> = {}
      if (data.percentage && Math.abs(Number(data.percentage)) > 100) {
        errors.percentage = i18n.t('finance.modals.new_tax_type.errors.amount_validation_v2')
      }

      return errors
    },

    onSubmit: async (values) => {
      setSubmitting(true)
      const result = await createTaxRate({
        rate: Number(values.percentage) / 100,
        description: values.description,
        taxTypeId,
      })

      const handled = handleResult(result, (errors) => {
        if (errors['amount']) {
          form.setErrors({
            percentage: errors['amount'],
          })
        } else {
          app.setFlash({
            type: 'wrong',
            message: i18n.t('finance.settings.taxes.tax_type.add_tax_rate_modal.messages.error'),
          })
        }
        track(trackingEvents.accounting.addValueSubmit, {
          status: 'Error',
          tax_name: taxType?.name,
          value_percentage: Number(values.percentage) / 100,
          value_description: values.description,
        })
        setSubmitting(false)
      })

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('finance.settings.taxes.tax_type.add_tax_rate_modal.messages.success'),
        })
        track(trackingEvents.accounting.addValueSubmit, {
          status: 'Success',
          tax_name: taxType?.name,
          value_percentage: Number(values.percentage) / 100,
          value_description: values.description,
        })
        onClose()
      }
      return handled
    },
  })

  return (
    <Modal
      illustration="list-document"
      title={
        taxType?.name
          ? i18n.t('finance.settings.taxes.tax_type.add_tax_rate_modal.title', {
              tax_name: taxType.name,
            })
          : ''
      }
      description={i18n.t('finance.settings.taxes.tax_type.add_tax_rate_modal.description')}
      onClose={onClose}
      onSubmit={() => form.handleSubmit()}
      footer={
        taxType && (
          <Button submit disabled={!form.isValid || submitting}>
            {i18n.t('finance.settings.taxes.tax_type.add_tax_rate_modal.buttons.add_tax_rate', {
              tax_name: taxType.name,
            })}
          </Button>
        )
      }
    >
      <Loading loading={submitting || !taxType}>
        <Box paddingBottom="s24">
          <TextField
            mandatory
            type="number"
            min={-100}
            step={'0.01'}
            label={i18n.t('finance.settings.taxes.tax_type.add_tax_rate_modal.fields.percentage')}
            value={form.values.percentage}
            onChange={(value) => form.setFieldValue('percentage', value)}
            error={form.errors.percentage}
          />
          <TextField
            label={i18n.t('finance.settings.taxes.tax_type.add_tax_rate_modal.fields.description')}
            value={form.values.description}
            onChange={(value) => form.setFieldValue('description', value)}
            error={form.errors.description}
          />
        </Box>
      </Loading>
    </Modal>
  )
}

export default observer(NewTaxRateModal)
